import {apiRequest, ApiError} from "./client";
import type {
  AddCartResponse,
  Brand,
  CartDetailsResponse,
  CatalogFiltersResponse,
  CategoriesResponse,
  HomepageResponse,
  HomeBannerCategoriesResponse,
  InviteCodeResponse,
  Currency,
  CurrencyResponse,
  LangResponse,
  Product,
  ProductDetail,
  ProductListParams,
  ProductListResponse,
  ProductReview,
  SessionUser,
  ShippingHintResponse,
  SuggestResponse,
  SearchRichResponse,
  WishlistResponse
} from "./types";

export async function login(email: string, parola: string): Promise<SessionUser> {
  return apiRequest<SessionUser>("/auth/login", {method: "POST", body: {email, parola}});
}

export async function register(data: {
  nume: string;
  lnume: string;
  email: string;
  telefon: string;
  parola: string;
  cod?: string;
  firma?: number;
}): Promise<SessionUser> {
  return apiRequest<SessionUser>("/auth/register", {method: "POST", body: data});
}

export async function validateInviteCode(cod: string): Promise<InviteCodeResponse> {
  return apiRequest<InviteCodeResponse>("/auth/register/validate-code", {method: "POST", body: {cod}});
}

export async function logout(): Promise<void> {
  await apiRequest<{ok: boolean}>("/auth/logout", {method: "POST"});
}

export async function getSession(): Promise<SessionUser> {
  try {
    return await apiRequest<SessionUser>("/auth/session");
  } catch(e) {
    if(e instanceof ApiError && e.status === 401) return {authenticated: false};
    throw e;
  }
}

function buildQuery(params?: Record<string, string | number | undefined>): string {
  if(!params) return "";
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if(v !== undefined && v !== "") q.set(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : "";
}

function appendListParams(q: URLSearchParams, params?: ProductListParams): void {
  if(!params) return;
  Object.entries(params).forEach(([k, v]) => {
    if(v === undefined || v === "") return;
    if(k === "filters" && v && typeof v === "object") {
      Object.entries(v as Record<string, number[]>).forEach(([fid, vals]) => {
        vals.forEach((val) => q.append(`${fid}[]`, String(val)));
      });
    } else if(k === "pret" && Array.isArray(v)) {
      v.forEach((val) => q.append("pret[]", String(val)));
    } else if(k === "ids" && Array.isArray(v)) {
      q.set("ids", v.join(","));
    } else if((k === "pretMin" || k === "pretMax") && v !== undefined && v !== "") {
      q.set(k, String(v));
    } else if(typeof v === "number" || typeof v === "string") {
      q.set(k, String(v));
    }
  });
}

export async function listProducts(params?: ProductListParams): Promise<ProductListResponse> {
  const q = new URLSearchParams();
  appendListParams(q, params);
  const s = q.toString();
  return apiRequest<ProductListResponse>(`/products${s ? `?${s}` : ""}`);
}

export async function listProductsInfinite(params?: ProductListParams): Promise<ProductListResponse> {
  return apiRequest<ProductListResponse>("/products/infinite", {method: "POST", body: params || {}});
}

export async function getCatalogFilters(params?: ProductListParams): Promise<CatalogFiltersResponse> {
  const q = new URLSearchParams();
  appendListParams(q, params);
  const s = q.toString();
  return apiRequest<CatalogFiltersResponse>(`/catalog/filters${s ? `?${s}` : ""}`);
}

export async function getHomepage(): Promise<HomepageResponse> {
  return apiRequest<HomepageResponse>("/homepage");
}

export async function getProduct(id: number): Promise<ProductDetail> {
  return apiRequest<ProductDetail>(`/products/${id}`);
}

export async function searchSuggest(q: string): Promise<SuggestResponse> {
  return apiRequest<SuggestResponse>(`/search/suggest${buildQuery({s: q})}`);
}

export async function searchRich(q: string): Promise<SearchRichResponse> {
  return apiRequest<SearchRichResponse>(`/search/rich${buildQuery({s: q})}`);
}

export async function getCategories(): Promise<CategoriesResponse> {
  return apiRequest<CategoriesResponse>("/categories");
}

export async function getHomeBannerCategories(): Promise<HomeBannerCategoriesResponse> {
  return apiRequest<HomeBannerCategoriesResponse>("/categories/home-banners");
}

export async function addToCart(item: {
  id_produs: number;
  _pret: number;
  cantitate: number;
  _locatie: string;
}): Promise<AddCartResponse> {
  return apiRequest<AddCartResponse>("/cart/items", {method: "POST", body: item});
}

export async function getCart(judet?: number): Promise<CartDetailsResponse> {
  const q = judet && judet > 0 ? `?judet=${judet}` : "";
  return apiRequest<CartDetailsResponse>(`/cart${q}`);
}

export async function getShippingHint(): Promise<ShippingHintResponse> {
  return apiRequest<ShippingHintResponse>("/cart/shipping-hint");
}

export async function resetCart(): Promise<CartDetailsResponse> {
  return apiRequest<CartDetailsResponse>("/cart", {method: "DELETE"});
}

export async function updateCartBulk(items: {warehouse: string; productId: number; quantity: number}[]): Promise<CartDetailsResponse> {
  return apiRequest<CartDetailsResponse>("/cart", {method: "PATCH", body: {items}});
}

export async function updateCartDynamic(data: {id_produs: number; cantitate: number; _pret: number; filtru?: number[]}): Promise<CartDetailsResponse> {
  return apiRequest<CartDetailsResponse>("/cart/dynamic", {method: "PATCH", body: data});
}

export async function removeCartItem(warehouse: string, productId: number): Promise<CartDetailsResponse> {
  return apiRequest<CartDetailsResponse>(`/cart/items/${encodeURIComponent(warehouse)}/${productId}`, {method: "DELETE"});
}

export async function updateCartItem(warehouse: string, productId: number, quantity: number): Promise<CartDetailsResponse> {
  return apiRequest<CartDetailsResponse>(`/cart/items/${encodeURIComponent(warehouse)}/${productId}`, {
    method: "PATCH",
    body: {quantity}
  });
}

export async function getWishlist(): Promise<WishlistResponse> {
  return apiRequest<WishlistResponse>("/wishlist");
}

export async function toggleWishlist(id: number): Promise<{count: number}> {
  return apiRequest<{count: number}>(`/wishlist/${id}`, {method: "POST"});
}

export async function clearWishlist(): Promise<{count: number}> {
  return apiRequest<{count: number}>("/wishlist", {method: "DELETE"});
}

export async function getLang(): Promise<LangResponse> {
  return apiRequest<LangResponse>("/i18n/lang");
}

export async function setLang(lang: "ro" | "en"): Promise<LangResponse> {
  return apiRequest<LangResponse>("/i18n/lang", {method: "POST", body: {lang}});
}

export async function getCurrency(): Promise<CurrencyResponse> {
  return apiRequest<CurrencyResponse>("/i18n/currency");
}

export async function setCurrency(currency: Currency): Promise<CurrencyResponse> {
  return apiRequest<CurrencyResponse>("/i18n/currency", {method: "POST", body: {currency}});
}

export function productDisplayPrice(pretEuro: string, pretLei: string, currency: Currency): string {
  return currency === "eur" ? pretEuro : pretLei;
}

export const DEFAULT_EUR_RATE = 4.97;

export function leiToEur(lei: number, eurRate?: number): number {
  const rate = eurRate && eurRate > 0 ? eurRate : DEFAULT_EUR_RATE;
  return Math.round((lei / rate) * 100) / 100;
}

/** Format a lei amount for UI labels (cards, buy box, DsPrice strings). */
export function formatPriceLabel(lei: number, currency: Currency, eurRate?: number, leiLabel = "Lei"): string {
  const roundedLei = Math.round(lei * 100) / 100;
  if(currency === "eur") return `${leiToEur(roundedLei, eurRate).toFixed(2)} €`;
  return `${roundedLei.toFixed(2)} ${leiLabel}`;
}

export function formatMoneyDisplay(lei: number, currency: Currency, eurRate?: number): string {
  const roundedLei = Math.round(lei * 100) / 100;
  if(currency === "eur") return `${leiToEur(roundedLei, eurRate).toFixed(2)} EUR`;
  return `${roundedLei.toFixed(2)} lei`;
}

export async function getBrands(): Promise<{items: Brand[]}> {
  return apiRequest<{items: Brand[]}>("/brands");
}

export async function addCartVoucher(code: string): Promise<CartDetailsResponse> {
  return apiRequest<CartDetailsResponse>("/cart/vouchers", {method: "POST", body: {voucher: code}});
}

export async function removeCartVoucher(id: number): Promise<CartDetailsResponse> {
  return apiRequest<CartDetailsResponse>(`/cart/vouchers/${id}`, {method: "DELETE"});
}

export async function getProductReviews(id: number): Promise<{items: ProductReview[]}> {
  return apiRequest<{items: ProductReview[]}>(`/products/${id}/reviews`);
}

export async function submitProductReview(id: number, data: {
  nume: string;
  email: string;
  telefon?: string;
  mesaj: string;
  stars: number;
  cod: number;
}): Promise<{ok: boolean; message?: string}> {
  return apiRequest<{ok: boolean; message?: string}>(`/products/${id}/reviews`, {method: "POST", body: {...data, nota: data.stars}});
}
